import type { Banco } from '@sheafyield/sync';
import { useNavigate } from '@tanstack/react-router';
import { useLayoutEffect, useRef, useState } from 'react';

import {
  banco as bancoDoApp,
  cliente,
  motor as motorDoApp,
} from '../app/contexto.js';
import {
  filtrosComConteudo,
  pessoasComUltimaVisita,
  type FiltroDePessoas,
} from '../dados/consultas.js';
import { useConsultaCom, type MotorObservavel } from '../dados/useConsulta.js';
import { donoDaSessao, type DonoDasLinhas } from '../dados/dono.js';
import { FolhaPessoa } from './FolhaPessoa.js';
import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { GrupoDeChips } from '../ds/GrupoDeChips.js';
import { Iniciais } from '../ds/Iniciais.js';
import { Linha } from '../ds/Linha.js';
import { Selecao } from '../ds/Selecao.js';
import { Vazio } from '../ds/Vazio.js';
import { haQuantoTempo, useDiaLocal } from '../hoje/relogio.js';
import { ROTULO_DO_RESULTADO } from '../hoje/VisitasDoDia.js';
import { ROTULO_DO_FILTRO, ROTULO_DO_STATUS } from './rotulos.js';
import './pessoas.css';

export interface DepsDePessoas {
  banco: Banco;
  motor: MotorObservavel;
  aoAbrirFicha: (id: string) => void;
  aoNovaPessoa: () => void;
}

export function PessoasTelaCom({ deps }: { deps: DepsDePessoas }) {
  const [termo, setTermo] = useState('');
  const [filtro, setFiltro] = useState<FiltroDePessoas>('todos');
  const [tentativa, setTentativa] = useState(0);
  const filtrosNaTela = useRef<HTMLDivElement>(null);
  const filtroFocado = useRef<HTMLInputElement | null>(null);
  const dia = useDiaLocal();
  const filtros = useConsultaCom(
    deps.motor,
    `filtros-de-pessoas:${String(tentativa)}`,
    () => filtrosComConteudo(deps.banco),
  );
  const oferecidos = filtros.dados;
  useLayoutEffect(() => {
    const anterior = filtroFocado.current;
    // Remover o rádio não dispara blur: o navegador larga o foco no body.
    // Só esse desaparecimento autoriza encaminhar a Todos. Quem já saiu
    // para a busca, o seletor ou outro controle mantém o próprio foco.
    if (anterior !== null && !anterior.isConnected) {
      filtroFocado.current = null;
      if (document.activeElement === document.body) {
        filtrosNaTela.current
          ?.querySelector<HTMLInputElement>('input[value="todos"]')
          ?.focus();
      }
    }
  }, [oferecidos]);
  // Uma sincronização ou exclusão pode esvaziar o filtro escolhido. Corrige
  // no render: a lista nunca chega a pintar outro recorte sob o chip antigo.
  if (
    oferecidos !== undefined &&
    filtro !== 'todos' &&
    !oferecidos.includes(filtro)
  ) {
    setFiltro('todos');
  }
  // Termo, filtro e dia fazem parte da pergunta: trocar um deles invalida a
  // resposta antiga. O dia atualiza “hoje/ontem” ao voltar ao app ou à meia-noite.
  const lista = useConsultaCom(
    deps.motor,
    `pessoas:${filtro}:${termo}:${dia}:${String(tentativa)}`,
    () => pessoasComUltimaVisita(deps.banco, termo, filtro),
  );
  const erro = filtros.erro ?? lista.erro;
  const opcoes = (oferecidos ?? []).map((valor) => ({
    valor,
    rotulo: ROTULO_DO_FILTRO[valor],
  }));

  function conteudo() {
    if (oferecidos === undefined) {
      return erro === null ? <p role="status">Carregando pessoas…</p> : null;
    }
    if (oferecidos.length === 0) {
      return (
        <Vazio
          nivel={2}
          titulo="Ninguém por aqui ainda"
          descricao="As pessoas que você registrar no campo aparecem aqui, com o histórico de cada visita."
          acao={
            <Botao largura="cheia" onClick={deps.aoNovaPessoa}>
              Adicionar pessoa
            </Botao>
          }
        />
      );
    }
    return (
      <>
        <Botao
          className="pessoas__adicionar"
          variante="texto"
          onClick={deps.aoNovaPessoa}
        >
          Adicionar pessoa
        </Botao>
        <Campo
          rotulo="Buscar nome ou endereço"
          type="search"
          value={termo}
          onChange={(evento) => setTermo(evento.target.value)}
        />
        <div
          className="pessoas__filtros"
          ref={filtrosNaTela}
          onFocusCapture={(evento) => {
            filtroFocado.current =
              evento.target instanceof HTMLInputElement ? evento.target : null;
          }}
          onBlurCapture={() => {
            filtroFocado.current = null;
          }}
        >
          <GrupoDeChips
            className="pessoas__chips"
            rotulo="Filtrar pessoas"
            nome="filtro-de-pessoas"
            valor={filtro}
            aoMudar={setFiltro}
            opcoes={opcoes}
          />
          <Selecao
            className="pessoas__seletor"
            rotulo="Filtrar pessoas"
            valor={filtro}
            aoMudar={(valor) => setFiltro(valor as FiltroDePessoas)}
            opcoes={opcoes}
          />
        </div>
        {lista.dados === undefined ? (
          erro === null ? (
            <p role="status">Carregando pessoas…</p>
          ) : null
        ) : lista.dados.length === 0 ? (
          <div role="status">
            <Vazio
              titulo="Nenhuma pessoa com esse nome"
              descricao="Confira a grafia, ou busque pelo endereço. Você também pode mudar o filtro."
            />
          </div>
        ) : (
          <ul className="pessoas__lista" aria-label="Pessoas">
            {lista.dados.map(({ pessoa, ultimaVisita }) => {
              const titulo =
                pessoa.nome.trim() ||
                pessoa.endereco.trim() ||
                'Pessoa sem nome';
              const detalhe =
                ultimaVisita === null
                  ? `${ROTULO_DO_STATUS[pessoa.status]} · Sem visitas registradas`
                  : `${ROTULO_DO_RESULTADO[ultimaVisita.resultado]} · ${haQuantoTempo(ultimaVisita.em, new Date(dia))}`;
              return (
                <Linha
                  key={pessoa.id}
                  titulo={titulo}
                  detalhe={detalhe}
                  meta={<Iniciais nome={titulo} />}
                  aoTocar={() => deps.aoAbrirFicha(pessoa.id)}
                />
              );
            })}
          </ul>
        )}
      </>
    );
  }

  return (
    <div className="pessoas">
      {erro !== null ? (
        <div className="pessoas__erro">
          <p role="alert">Não deu para ler as pessoas. Tente novamente.</p>
          <Botao
            variante="secundario"
            onClick={() => setTentativa((anterior) => anterior + 1)}
          >
            Tentar novamente
          </Botao>
        </div>
      ) : null}
      {conteudo()}
    </div>
  );
}

const donoDoApp = donoDaSessao(cliente);

export function PessoasComFormulario({
  deps,
}: {
  deps: Omit<DepsDePessoas, 'aoNovaPessoa'> & { dono: DonoDasLinhas };
}) {
  const [folhaAberta, setFolhaAberta] = useState(false);
  return (
    <>
      <PessoasTelaCom
        deps={{ ...deps, aoNovaPessoa: () => setFolhaAberta(true) }}
      />
      <FolhaPessoa
        banco={deps.banco}
        dono={deps.dono}
        pessoa={null}
        aberta={folhaAberta}
        aoFechar={() => setFolhaAberta(false)}
      />
    </>
  );
}

export function PessoasTela() {
  const navegar = useNavigate();
  return (
    <PessoasComFormulario
      deps={{
        banco: bancoDoApp,
        motor: motorDoApp,
        dono: donoDoApp,
        aoAbrirFicha: (id) => {
          void navegar({ to: '/pessoas/$id', params: { id } });
        },
      }}
    />
  );
}
